import { SetupTaskId } from '../setupTasksData'
import { SetupStep } from './d'
import { $RootStore } from '../../../../src/stores/RootStore'
import { StripeAccountStatus } from '../../../../graphQL/generated/enums'

export const PaymentsAndDepositsStepIds = {
  onlineCardPayments: `${SetupTaskId.PaymentsAndDesposits}-setUpStripe`,
  depositRules: `${SetupTaskId.PaymentsAndDesposits}-depositRules`,
  giftCard: `${SetupTaskId.PaymentsAndDesposits}-giftCard`,
  tapToPay: `${SetupTaskId.PaymentsAndDesposits}-tapToPay`,
} as const

const paymentsAndDepositsSteps: SetupStep[] = [
  {
    title: 'Online card payments',
    description: 'Take secure card payments online to make booking simple and convenient for clients.',
    id: PaymentsAndDepositsStepIds.onlineCardPayments,
    taskId: SetupTaskId.PaymentsAndDesposits,
    icon: 'stripe',
    check: (rootStore: $RootStore) => {
      const { stripeAccountStatus } = rootStore.userStore.data
      return !!stripeAccountStatus && stripeAccountStatus === StripeAccountStatus.Completed
    },
    __reset: async (rootStore: $RootStore) => {
      const { userStore, bottomMessageHandler, i18n: { s } } = rootStore
      await userStore.updateSaasUser_api({ stripeAccountStatus: StripeAccountStatus.NotSubmitted })
      await userStore.getProfessionalWithStripe_api()
      bottomMessageHandler.showBottomMessage({
        title: s('Your Stripe status has been reset'),
        body: s('To complete the Stripe account again, update your name on Stripe.'),
        icon: {
          type: 'circleIcon',
          iconName: 'info',
          color: 'primary',
        },
      })
    },
    startAction: async (_rootStore: $RootStore) => {
      // Platform-specific action defined in stepData.ts
    },
    toastData: {
      title: 'Stripe account verification in progress..',
      nextStepId: SetupTaskId.PaymentsAndDesposits,
    },
    dependsOnStepIds: [],
  },
  {
    title: 'Booking deposits',
    description: 'Require deposits to reduce no-shows and secure your income in advance.',
    id: PaymentsAndDepositsStepIds.depositRules,
    taskId: SetupTaskId.PaymentsAndDesposits,
    icon: 'deposit',
    check: (rootStore: $RootStore) => {
      const { clientDepositSettings } = rootStore.userStore.data.professionalProfile.saasSettings
      return (clientDepositSettings?.length || 0) > 0
    },
    __reset: async (rootStore: $RootStore) => {
      await rootStore.userStore.updateSaasSettings_api({
        clientDepositSettings: [],
      })
    },
    startAction: async (_rootStore: $RootStore) => {
      // Platform-specific action defined in stepData.ts
    },
    toastData: {
      title: 'You have set up your deposit settings!',
      nextStepId: SetupTaskId.PaymentsAndDesposits,
    },
    dependsOnStepIds: [PaymentsAndDepositsStepIds.onlineCardPayments],
  },
  {
    title: 'Gift cards',
    description: 'Sell digital gift cards to increase revenue and attract new clients.',
    id: PaymentsAndDepositsStepIds.giftCard,
    taskId: SetupTaskId.PaymentsAndDesposits,
    icon: 'gift',
    check: (_rootStore: $RootStore) => {
      // Platform-specific check defined in stepData.ts
      return false
    },
    startAction: async (_rootStore: $RootStore) => {
      // Platform-specific action defined in stepData.ts
    },
    __reset: async (rootStore: $RootStore) => {
      await rootStore.userStore.updateProfessionalProfile_api({
        giftCardSettings: { enabled: false, daysBeforeExpires: 365 },
      })
    },
    toastData: {
      title: 'You have enabled your clients to purchase gift cards.',
      nextStepId: SetupTaskId.PaymentsAndDesposits,
    },
    dependsOnStepIds: [PaymentsAndDepositsStepIds.onlineCardPayments],
  },
  {
    title: 'Tap to pay',
    description: 'Accept contactless payments in person using just your phone.',
    id: PaymentsAndDepositsStepIds.tapToPay,
    taskId: SetupTaskId.PaymentsAndDesposits,
    icon: 'contactless',
    skipOnPlatforms: ['SD'],
    hidden: (rootStore: $RootStore) => {
      const stripeCompleted = rootStore.userStore.data.stripeAccountStatus === StripeAccountStatus.Completed
      return !stripeCompleted || !rootStore.canAccessTTP
    },
    check: (_rootStore: $RootStore) => {
      // Platform-specific check defined in stepData.ts
      return false
    },
    __reset: async (rootStore: $RootStore) => {
      const uiStore = rootStore.uiStore as any
      uiStore.setTapToPayState?.('eligible')
    },
    startAction: async (_rootStore: $RootStore) => {
      // Platform-specific action defined in stepData.ts
    },
    toastData: (rootStore: $RootStore) => ({
      title: rootStore.canAccessTTP
        ? 'You have set up Tap to Pay.'
        : 'It looks like your device does not have access so this step has been skipped.',
      nextStepId: SetupTaskId.PaymentsAndDesposits,
    }),
    dependsOnStepIds: [PaymentsAndDepositsStepIds.onlineCardPayments],
  },
]

export default paymentsAndDepositsSteps
